import React, { useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material';
import { loadUser, logout } from '../store/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useRBAC } from '../utils/rbac';

/** Nur ein loadUser() pro Session – viele ProtectedRoute-Instanzen würden sonst alle /auth/me aufrufen. */
let loadUserDispatchedThisSession = false;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
  requiredPermissions?: string[];
  requiredAction?: string;
  requiredResource?: string;
  resourceId?: string;
  sensitivityLevel?: 'normal' | 'sensitive' | 'highly_sensitive' | 'restricted';
  showAccessDenied?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredPermissions = [],
  requiredAction,
  requiredResource,
  resourceId,
  sensitivityLevel,
  showAccessDenied = true
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user, isAuthenticated, loading, token } = useAppSelector((state) => state.auth);
  const rbac = useRBAC(user);

  const handleRetryLoadUser = useCallback(() => {
    loadUserDispatchedThisSession = false;
    dispatch(loadUser());
  }, [dispatch]);

  const handleLogoutStuck = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    loadUserDispatchedThisSession = false;
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      loadUserDispatchedThisSession = false;
      return;
    }
    if (user || loading) return;
    if (loadUserDispatchedThisSession) return;
    loadUserDispatchedThisSession = true;
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Loading user with token (once per session)');
    }
    dispatch(loadUser());
  }, [dispatch, token, user, loading]);

  // Stuck state: Token vorhanden, aber loadUser ist fehlgeschlagen (z.B. Netzwerkfehler oder Session ungültig)
  // Statt Endlos-Ladekreis: Fehlerseite mit Retry und Abmelden
  if (token && !user && !loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ p: 3 }}
      >
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <Typography variant="h6" gutterBottom>
            Sitzung konnte nicht geladen werden
          </Typography>
          <Typography paragraph>
            Die Verbindung zum Server ist fehlgeschlagen oder Ihre Sitzung ist ungültig (z.B. weil Sie sich in einem anderen Browser angemeldet haben).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={handleRetryLoadUser}>
              Erneut versuchen
            </Button>
            <Button variant="outlined" onClick={handleLogoutStuck}>
              Abmelden
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Show loading spinner while checking authentication
  if (loading || (token && !user)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Not authenticated, redirecting to login', {
        isAuthenticated,
        hasUser: !!user,
        token: !!token,
        location: location.pathname
      });
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole) {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Checking role access', { 
        requiredRole, 
        userRole: user.role, 
        user: user,
        isAuthenticated,
        token: !!token
      });
    }
    
    if (Array.isArray(requiredRole) ? !requiredRole.some(role => rbac.hasRole(role)) : !rbac.hasRole(requiredRole)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('ProtectedRoute: Access denied - insufficient role');
      }
      if (showAccessDenied) {
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="h6">Zugriff verweigert</Typography>
              <Typography>
                Sie haben nicht die erforderliche Rolle für diesen Bereich.
                Erforderlich: {Array.isArray(requiredRole) ? requiredRole.join(', ') : requiredRole}
                <br />
                Ihre Rolle: {user.role}
              </Typography>
            </Alert>
          </Box>
        );
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check permission-based access (using new RBAC system)
  if (requiredPermissions.length > 0) {
    // Convert old permission format to new RBAC format (with validation)
    const rbacPermissions = requiredPermissions.map(perm => {
      // Convert old format like "patients.read" to new format
      if (perm.includes('.')) {
        const [resource, action] = perm.split('.');
        
        // Warnung in Development, wenn Plural-Format verwendet wird
        if (process.env.NODE_ENV === 'development') {
          const pluralResources = ['patients', 'users', 'appointments', 'documents', 'services', 'locations', 'bookings'];
          if (pluralResources.includes(resource)) {
            console.warn(
              `[ProtectedRoute] Veraltetes Plural-Format erkannt: "${perm}". ` +
              `Bitte verwenden Sie Singular-Format (z.B. "patient.read" statt "patients.read")`
            );
          }
        }
        
        // Convert plural resources to singular for consistency with backend
        const resourceMapping: Record<string, string> = {
          'patients': 'patient',
          'users': 'user',
          'appointments': 'appointment',
          'documents': 'document',
          'diagnoses': 'diagnosis',
          'prescriptions': 'prescription',
          'billings': 'billing',
          'roles': 'role',
          'locations': 'location',
          'services': 'service',
          'templates': 'template',
          'audit_logs': 'audit_log',
          'bookings': 'appointment', // bookings wird zu appointment
          'reports': 'reports', // Bleibt Plural
          'staff': 'staff', // Bleibt Singular
          'systems': 'system',
          'settings': 'settings', // Bleibt Plural
          'security': 'security' // Bleibt Singular
        };
        const normalizedResource = resourceMapping[resource] || resource;
        
        // Validiere, ob Resource und Action gültig sind
        const validResources = ['patient', 'appointment', 'document', 'service', 'location', 'user', 'billing', 'role', 'template', 'staff', 'settings', 'security', 'reports', 'system', 'audit_log', 'diagnosis', 'prescription'];
        if (!validResources.includes(normalizedResource)) {
          console.error(`[ProtectedRoute] Ungültige Resource: "${normalizedResource}" in Permission "${perm}"`);
        }
        
        return { action, resource: normalizedResource };
      }
      return { action: perm, resource: 'system' };
    });
    
    // Use the new RBAC hook for permission checking
    const hasRequiredPermission = rbacPermissions.some(({ action, resource }) => {
      // Use the can function from useRBAC hook (correct signature: can(action, resource))
      return rbac.can(action, resource);
    });
    
    if (!hasRequiredPermission) {
      if (process.env.NODE_ENV === 'development') {
        console.log('ProtectedRoute: Permission check failed', {
          requiredPermissions,
          rbacPermissions,
          userRole: user.role,
          userPermissions: user.permissions
        });
      }
      if (showAccessDenied) {
        // Get available permissions for the first required resource
        const firstResource = rbacPermissions[0]?.resource || 'user';
        const availablePermissions = user.permissions?.filter(p => p.startsWith(`${firstResource}.`)) || [];
        
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="h6">Zugriff verweigert</Typography>
              <Typography>
                Sie haben nicht die erforderlichen Berechtigungen für diesen Bereich.
                <br />
                Erforderlich: {requiredPermissions.join(', ')}
                <br />
                Ihre Rolle: {user.role}
                <br />
                Verfügbare Berechtigungen für {firstResource}: {availablePermissions.length > 0 ? availablePermissions.join(', ') : 'Keine'}
                <br />
                <br />
                Debug Info:
                <br />
                Konvertierte Permissions: {JSON.stringify(rbacPermissions)}
                <br />
                RBAC Status: Aktiv
              </Typography>
            </Alert>
          </Box>
        );
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check action-resource based access
  if (requiredAction && requiredResource) {
    if (!rbac.can(requiredAction, requiredResource, resourceId)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('ProtectedRoute: Action-Resource check failed', {
          requiredAction,
          requiredResource,
          resourceId,
          userRole: user.role
        });
      }
      if (showAccessDenied) {
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="h6">Zugriff verweigert</Typography>
              <Typography>
                Sie haben nicht die Berechtigung, diese Aktion auszuführen.
                <br />
                Erforderlich: {requiredAction} auf {requiredResource}
                {resourceId && ` (ID: ${resourceId})`}
              </Typography>
            </Alert>
          </Box>
        );
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check sensitivity level access
  if (sensitivityLevel) {
    // For now, only super_admin can access highly sensitive data
    if (sensitivityLevel === 'highly_sensitive' && user.role !== 'super_admin') {
      if (process.env.NODE_ENV === 'development') {
        console.log('ProtectedRoute: Sensitivity level check failed', {
          sensitivityLevel,
          userRole: user.role
        });
      }
      if (showAccessDenied) {
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="h6">Zugriff verweigert</Typography>
              <Typography>
                Sie haben nicht die Berechtigung, auf diese sensiblen Daten zuzugreifen.
                <br />
                Erforderliche Berechtigung: {sensitivityLevel} Daten
                <br />
                Ihre Rolle: {user.role}
              </Typography>
            </Alert>
          </Box>
        );
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Debug log for successful authentication
  if (process.env.NODE_ENV === 'development') {
    console.log('ProtectedRoute: Access granted', { 
      userRole: user.role, 
      requiredRole, 
      requiredPermissions,
      requiredAction,
      requiredResource,
      sensitivityLevel,
      user: user,
      location: location.pathname
    });
  }

  return <>{children}</>;
};

export default ProtectedRoute;